import Vocabulary from "components/Vocabulary";
import _, { isEmpty } from "lodash";
import React from "react";
import { FieldTypes } from "utils/formUtils";
import parseHtml from "utils/parserUtil";

import { ToolMapField } from "../CustomToolForm";
import {
  JIRA_SECURITY_TOOL_MAPPING_MULTI_SUPPORTED_FIELDS,
  MAPPING_FIELD_FORM_KEYS,
  PRIORITY_FIELD_KEYS,
  STATUS_FIELD_KEYS,
} from "./JiraSecurityToolConstants";
import {
  JiraSecurityToolMappingToolFieldProps,
  JiraSecurityToolRecordProps,
  MappingFieldProps,
  SelectFieldOptionProps,
  SeverityMapingProps,
} from "./JiraSecurityToolPropTypes";

const commonPriorityFieldProps = {
  colSpan: 6,
  labelCol: { span: 24 },
  wrapperCol: { span: 24 },
  props: {
    allowClear: true,
  },
};

const statusSeverityProps = {
  colSpan: 8,
  labelCol: { span: 24 },
  wrapperCol: { span: 24 },
  style: { flex: "33% 33% 33%" },
};

export const getMappingToolFields = ({
  fieldOptions,
  dateTypeFieldOptions,
  numberTypeFieldOptions,
}: {
  fieldOptions: { label: string; value: string }[];
  dateTypeFieldOptions: { label: string; value: string }[];
  numberTypeFieldOptions: { label: string; value: string }[];
}) => {
  const fields = [
    {
      name: "summary",
      label: ToolMapField.SUMMARY,
      required: true,
      colSpan: 12,
      labelCol: { span: 8 },
      wrapperCol: { span: 17 },
      className: "custom-tool-label",
      options: fieldOptions,
      type: FieldTypes.MULTI_SELECT,
      props: {
        allowClear: true,
      },
    },
    {
      name: "description",
      label: ToolMapField.DESCRIPTION,
      required: true,
      colSpan: 12,
      labelCol: { span: 8 },
      wrapperCol: { span: 17 },
      className: "custom-tool-label",
      options: fieldOptions,
      type: FieldTypes.MULTI_SELECT,
      props: {
        allowClear: true,
      },
    },
    {
      name: "mitigation",
      label: ToolMapField.MITIGATION,
      colSpan: 12,
      labelCol: { span: 8 },
      wrapperCol: { span: 17 },
      className: "custom-tool-label",
      options: fieldOptions,
      type: FieldTypes.MULTI_SELECT,
      props: {
        allowClear: true,
      },
    },
    {
      name: "cwe",
      label: ToolMapField.CWE,
      colSpan: 12,
      labelCol: { span: 8 },
      wrapperCol: { span: 17 },
      className: "custom-tool-label",
      options: fieldOptions,
      type: FieldTypes.SELECT,
      props: {
        allowClear: true,
      },
    },
    {
      name: "cve",
      label: ToolMapField.CVE,
      colSpan: 12,
      labelCol: { span: 8 },
      wrapperCol: { span: 17 },
      className: "custom-tool-label",
      options: fieldOptions,
      type: FieldTypes.SELECT,
      props: {
        allowClear: true,
      },
    },
    {
      name: "impact",
      label: ToolMapField.IMPACT,
      colSpan: 12,
      labelCol: { span: 8 },
      wrapperCol: { span: 17 },
      className: "custom-tool-label",
      options: fieldOptions,
      type: FieldTypes.MULTI_SELECT,
      props: {
        allowClear: true,
      },
    },
    {
      name: "uniqueIdFromTool",
      label: ToolMapField.UNIQUE_ID_FROM_TOOL,
      colSpan: 12,
      labelCol: { span: 8 },
      wrapperCol: { span: 17 },
      className: "custom-tool-label",
      options: fieldOptions,
      type: FieldTypes.SELECT,
      props: {
        allowClear: true,
      },
    },
    // for a customer bug this field is removed,
    // will be added back if required in future
    // {
    //   name: "scanDate",
    //   label: "Scan Date",
    //   colSpan: 12,
    //   labelCol: { span: 8 },
    //   wrapperCol: { span: 17 },
    //   className: "custom-tool-label",
    //   options: dateTypeFieldOptions,
    //   type: FieldTypes.SELECT,
    //   props: {
    //     allowClear: true,
    //   },
    // },
    {
      name: "foundOnDate",
      label: "Found On Date",
      colSpan: 12,
      labelCol: { span: 8 },
      wrapperCol: { span: 17 },
      className: "custom-tool-label",
      options: dateTypeFieldOptions,
      type: FieldTypes.SELECT,
      props: {
        allowClear: true,
      },
    },
    {
      name: "resolutionDueDate",
      label: "Resolution Due Date",
      colSpan: 12,
      labelCol: { span: 8, className: "break-content" },
      wrapperCol: { span: 17 },
      className: "custom-tool-label",
      options: dateTypeFieldOptions,
      type: FieldTypes.SELECT,
      props: {
        allowClear: true,
      },
    },
    {
      name: "device",
      label: ToolMapField.DEVICE,
      colSpan: 12,
      labelCol: { span: 8 },
      wrapperCol: { span: 17 },
      className: "custom-tool-label",
      options: fieldOptions,
      type: FieldTypes.SELECT,
      props: {
        allowClear: true,
      },
    },
    {
      name: "componentName",
      label: ToolMapField.COMPONENT_NAME,
      colSpan: 12,
      labelCol: { span: 8, className: "break-content" },
      wrapperCol: { span: 17 },
      className: "custom-tool-label",
      options: fieldOptions,
      type: FieldTypes.SELECT,
      props: {
        allowClear: true,
      },
    },
    {
      name: "componentVersion",
      label: parseHtml(ToolMapField.COMPONENT_VERSION.split(" ").join("<br/>")),
      colSpan: 12,
      labelCol: { span: 8 },
      wrapperCol: { span: 17 },
      className: "custom-tool-label",
      options: fieldOptions,
      type: FieldTypes.SELECT,
      placeholder: "Component Version",
      props: {
        allowClear: true,
      },
    },
    {
      name: "componentFixVersion",
      label: "Component Fix Version",
      colSpan: 12,
      labelCol: { span: 8, className: "break-content" },
      wrapperCol: { span: 17 },
      className: "custom-tool-label",
      options: fieldOptions,
      type: FieldTypes.SELECT,
      props: {
        allowClear: true,
      },
    },
    {
      name: "url",
      label: ToolMapField.URL,
      colSpan: 12,
      labelCol: { span: 8 },
      wrapperCol: { span: 17 },
      className: "custom-tool-label",
      options: fieldOptions,
      type: FieldTypes.SELECT,
      props: {
        allowClear: true,
      },
    },
    {
      name: "tags",
      label: ToolMapField.TAGS,
      colSpan: 12,
      labelCol: { span: 8 },
      wrapperCol: { span: 17 },
      className: "custom-tool-label",
      options: fieldOptions,
      type: FieldTypes.TAGS,
      props: {
        allowClear: true,
      },
    },
    {
      name: "repository",
      label: ToolMapField.REPOSITORY,
      colSpan: 12,
      labelCol: { span: 8 },
      wrapperCol: { span: 17 },
      className: "custom-tool-label",
      options: fieldOptions,
      type: FieldTypes.SELECT,
      props: {
        allowClear: true,
      },
    },
    {
      name: "scanType",
      label: "Scan Type",
      colSpan: 12,
      labelCol: { span: 8 },
      wrapperCol: { span: 17 },
      className: "custom-tool-label",
      options: fieldOptions,
      type: FieldTypes.SELECT,
      props: {
        allowClear: true,
      },
    },
    {
      name: "category",
      label: "Category",
      colSpan: 12,
      labelCol: { span: 8 },
      wrapperCol: { span: 17 },
      className: "custom-tool-label",
      options: fieldOptions,
      type: FieldTypes.SELECT,
      props: {
        allowClear: true,
      },
    },
    {
      name: "developer",
      label: ToolMapField.DEVELOPER,
      colSpan: 12,
      labelCol: { span: 8 },
      wrapperCol: { span: 17 },
      className: "custom-tool-label",
      options: fieldOptions,
      type: FieldTypes.SELECT,
      props: {
        allowClear: true,
      },
    },
    {
      name: "owner",
      label: "Owner",
      colSpan: 12,
      labelCol: { span: 8 },
      wrapperCol: { span: 17 },
      className: "custom-tool-label",
      options: fieldOptions,
      type: FieldTypes.SELECT,
      props: {
        allowClear: true,
      },
    },
    {
      name: "imageName",
      label: ToolMapField.IMAGE_NAME,
      colSpan: 12,
      labelCol: { span: 8 },
      wrapperCol: { span: 17 },
      className: "custom-tool-label",
      options: fieldOptions,
      type: FieldTypes.SELECT,
      props: {
        allowClear: true,
      },
    },
    {
      name: "imageTag",
      label: ToolMapField.IMAGE_TAG,
      colSpan: 12,
      labelCol: { span: 8 },
      wrapperCol: { span: 17 },
      className: "custom-tool-label",
      options: fieldOptions,
      type: FieldTypes.SELECT,
      props: {
        allowClear: true,
      },
    },
    {
      name: "vendor",
      label: ToolMapField.VENDOR,
      colSpan: 12,
      labelCol: { span: 8 },
      wrapperCol: { span: 17 },
      className: "custom-tool-label",
      options: fieldOptions,
      type: FieldTypes.SELECT,
      props: {
        allowClear: true,
      },
    },
    {
      name: "host",
      label: ToolMapField.HOST,
      colSpan: 12,
      labelCol: { span: 8 },
      wrapperCol: { span: 17 },
      className: "custom-tool-label",
      options: fieldOptions,
      type: FieldTypes.SELECT,
      props: {
        allowClear: true,
      },
    },
    {
      name: "cvssVector",
      label: ToolMapField.CVSS_VECTOR,
      colSpan: 12,
      labelCol: { span: 8 },
      wrapperCol: { span: 17 },
      className: "custom-tool-label",
      options: fieldOptions,
      type: FieldTypes.SELECT,
      props: {
        allowClear: true,
      },
    },
    {
      name: "cvssScore",
      label: ToolMapField.CVSS_SCORE,
      colSpan: 12,
      labelCol: { span: 8 },
      wrapperCol: { span: 17 },
      className: "custom-tool-label",
      options: numberTypeFieldOptions,
      type: FieldTypes.SELECT,
      props: {
        allowClear: true,
      },
    },
    {
      name: "status",
      label: "Status",
      colSpan: 12,
      labelCol: { span: 8 },
      wrapperCol: { span: 17 },
      className: "custom-tool-label",
      options: fieldOptions,
      type: FieldTypes.SELECT,
      props: {
        allowClear: true,
      },
    },
    {
      name: "stepsToReproduce",
      label: parseHtml(
        ToolMapField.STEPS_TO_REPRODUCE.split("_").join("<br/>"),
      ),
      colSpan: 12,
      labelCol: { span: 8 },
      wrapperCol: { span: 17 },
      className: "custom-tool-label",
      options: fieldOptions,
      type: FieldTypes.MULTI_SELECT,
      placeholder: "Steps To Reproduce",
      props: {
        allowClear: true,
      },
    },
    {
      name: "fileName",
      label: ToolMapField.FILE_NAME,
      colSpan: 12,
      labelCol: { span: 8 },
      wrapperCol: { span: 17 },
      className: "custom-tool-label",
      options: fieldOptions,
      type: FieldTypes.SELECT,
      props: {
        allowClear: true,
      },
    },
    {
      name: "filePath",
      label: ToolMapField.FILE_PATH,
      colSpan: 12,
      labelCol: { span: 8 },
      wrapperCol: { span: 17 },
      className: "custom-tool-label",
      options: fieldOptions,
      type: FieldTypes.SELECT,
      props: {
        allowClear: true,
      },
    },
    {
      name: "ticketId",
      label: ToolMapField.TICKET_ID,
      colSpan: 12,
      labelCol: { span: 8 },
      wrapperCol: { span: 17 },
      className: "custom-tool-label",
      options: fieldOptions,
      type: FieldTypes.SELECT,
      props: {
        allowClear: true,
      },
    },
    {
      name: "urlEndpoint",
      label: ToolMapField.URL_ENDPOINT,
      colSpan: 12,
      labelCol: { span: 8 },
      wrapperCol: { span: 17 },
      className: "custom-tool-label",
      options: fieldOptions,
      type: FieldTypes.SELECT,
      props: {
        allowClear: true,
      },
    },
    {
      name: "reopenDate",
      label: ToolMapField.REOPEN_DATE,
      colSpan: 12,
      labelCol: { span: 8 },
      wrapperCol: { span: 17 },
      className: "custom-tool-label",
      options: dateTypeFieldOptions,
      type: FieldTypes.SELECT,
      props: {
        allowClear: true,
      },
    },
  ];

  return fields;
};

export const generatePayload = (
  fieldsData: {
    toolFields: JiraSecurityToolMappingToolFieldProps[];
    subtaskFields: JiraSecurityToolMappingToolFieldProps[];
  },
  formData: any,
  loginConfig: any,
  productName: string,
  subProductName: string,
) => {
  let payload: any = {
    id: formData.id,
    jqlFilter: formData.jqlFilter,
    projectKey: formData.projectKey,
    issueType: formData.issueType,
    // product: formData.product,
    // subProduct: formData.subProduct,
    login_id: loginConfig.id,
    tool_name: "Jira",
    toolType: "PULL",
    environment: formData.environment,
    product_name: productName,
    sub_product_name: subProductName,
    ...(formData.onlySubtasks ? { onlySubtasks: formData.onlySubtasks } : {}),
  };
  const statusFieldsMapping = STATUS_FIELD_KEYS.reduce(
    (acc: any, key: string) => {
      if (formData[key]) {
        acc[key] = formData[key];
      }
      return acc;
    },
    {},
  );

  const priorityFieldMapping = PRIORITY_FIELD_KEYS.reduce(
    (acc: any, key: string) => {
      if (formData[key]) acc[key] = formData[key];
      return acc;
    },
    {},
  );

  let toolFieldsMapping: { [key: string]: any } = {};
  const fieldsToFetch: string[] = [];
  const isSubtasksOnly = formData.onlySubtasks;
  // if onlySubtasks is selected then severity provider field can contain prefix of parent or subtask
  // thus removing the prefix from field name for further filtering and mapping
  const customSeverityFieldName = formData.customSeverity;

  formData = _.omit({ ...formData }, ["customSeverity"]);
  // filter out only relevant mapping fields as well as remove undefined and empty fields
  let mappingFieldData = _.pick(formData, [
    ...Object.keys(MAPPING_FIELD_FORM_KEYS),
  ]);
  mappingFieldData = Object.fromEntries(
    Object.entries(mappingFieldData).filter(
      ([key, value]) => !_.isNil(value) || value,
    ),
  );
  for (const formDataKey in mappingFieldData) {
    let newMapping: { [key: string]: any[] } = {};
    let mappingField: JiraSecurityToolMappingToolFieldProps = {};
    // some fields are multi select
    const mappingFieldValue = Array.isArray(mappingFieldData[formDataKey])
      ? mappingFieldData[formDataKey]
      : [mappingFieldData[formDataKey]];
    mappingFieldValue.forEach((value: string) => {
      //since now using values for custom mapping fields as parent.<fieldName> or subtask.<fieldName>
      // so removing parent. or subtask. from field name
      let isSubtaskField = false;
      if (typeof value === "string" && value.startsWith("subtask.")) {
        isSubtaskField = true;
      }
      mappingField =
        fieldsData.toolFields.find(
          (f) => f.name === value.replace(/^(parent|subtask)\./, ""),
        ) || {};
      if (!isEmpty(mappingField)) {
        // @ts-expect-error
        if (!fieldsToFetch.includes(mappingField.key)) {
          // isSubtaskField - > value.startsWith("subtask.");
          // isSubtasksOnly - > Sub-Tasks only checkbox is selected;
          fieldsToFetch.push(
            isSubtaskField
              ? "subtask." + mappingField.key
              : !isSubtasksOnly
                ? mappingField.key!
                : "parent." + mappingField.key!,
          );
        }
        newMapping = {
          ...newMapping,
          [formDataKey]: [
            ...(newMapping[formDataKey] || []),
            {
              ticketFieldName: value,
              ticketFieldKey: isSubtaskField
                ? "subtask." + mappingField.key
                : !isSubtasksOnly
                  ? mappingField.key!
                  : "parent." + mappingField.key!,
              type: mappingField.type,
              dataType: mappingField.dataType,
            },
          ],
        };
      }
    });
    toolFieldsMapping = {
      ...toolFieldsMapping,
      ...newMapping,
    };
  }

  // if custom severity field is selected then it can be parent or subtask
  // thus removing the prefix from field name to find the severity provider field name from the mapping fields data
  // e.g field names Priority, Project, etc.
  const severityProviderFieldName =
    isSubtasksOnly && customSeverityFieldName
      ? customSeverityFieldName.replace(/^(parent|subtask)\./, "")
      : customSeverityFieldName;

  const selectedCustomSeverity = fieldsData.toolFields.find((f) => {
    return f.name === severityProviderFieldName;
  });

  let severityProviderFieldKey;
  const isSubtaskField = customSeverityFieldName?.startsWith("subtask.");

  if (
    selectedCustomSeverity?.key &&
    !fieldsToFetch.includes(selectedCustomSeverity.key)
  ) {
    // pre-pend subtask. or parent. to the field name and key
    // subtask -> if Subtask only checkbox is selected and field starts with subtask
    // parent -> if Subtask only checkbox is not selected and field starts with parent
    // else just the field key or name
    severityProviderFieldKey = isSubtaskField
      ? "subtask." + selectedCustomSeverity.key
      : !isSubtasksOnly
        ? selectedCustomSeverity.key!
        : "parent." + selectedCustomSeverity.key!;
    fieldsToFetch.push(severityProviderFieldKey);
  }
  payload = {
    ...payload,
    fieldMapping: {
      name: loginConfig.name,
      toolType: [formData.toolType],
      fieldMapping: toolFieldsMapping,
    },
    severityMapping: {
      ticketFieldName: customSeverityFieldName,
      ticketFieldKey: severityProviderFieldKey,
      otherProperties: priorityFieldMapping,
    },
    statusMapping: statusFieldsMapping,
    fieldsToFetch,
  };

  return payload;
};

export const getJiraSecurityToolFormInitialData = (
  data: JiraSecurityToolRecordProps,
) => {
  let finalFieldMappingdata: { [key: string]: any; onlySubtasks?: boolean } =
    {};
  if (data) {
    try {
      // @ts-expect-error
      const fieldMappingsJSON = JSON.parse(data?.fieldMapping as string);
      data.toolType = fieldMappingsJSON.toolType[0];
      const fieldMappings = fieldMappingsJSON.fieldMapping;
      if (fieldMappings && typeof fieldMappings === "object") {
        finalFieldMappingdata = Object.keys(fieldMappings || {}).reduce(
          (mapping: Record<string, any>, field: string) => {
            const fieldValue =
              fieldMappings[field as string as keyof MappingFieldProps]!;
            if (fieldValue) {
              mapping[field as string] =
                JIRA_SECURITY_TOOL_MAPPING_MULTI_SUPPORTED_FIELDS.includes(
                  field as string,
                )
                  ? fieldValue.map(
                      ({ ticketFieldName }: { ticketFieldName?: string }) =>
                        ticketFieldName,
                    )
                  : fieldValue[0]?.ticketFieldName;
            }
            return mapping;
          },
          {},
        );
      }
    } catch (error) {
      console.log(error);
    }
    //response comes as JSON string
    const severityMapping: SeverityMapingProps = JSON.parse(
      data?.severityMapping as string,
    );
    data = {
      ...data,
      product: data?.product_id,
      subProduct: data?.sub_product_id,
      ...finalFieldMappingdata,
      // @ts-expect-error
      ...(JSON.parse(data?.statusMapping as string) as {
        [key: string]: string;
      }),
      ...severityMapping?.otherProperties,
      customSeverity: severityMapping?.ticketFieldName,
      // @ts-expect-error
      onlySubtasks: data?.onlySubtasks === "true",
    };
    return data;
  }
  return {};
};

export const getSeverityCustomFieldOptios = (
  fields: JiraSecurityToolMappingToolFieldProps[],
) => {
  const fieldOptions = fields
    .filter((f) => f.allowedValues?.length)
    .map((f) => ({ id: f.name, name: f.name }));
  return fieldOptions;
};

// status fields are multiselect fields
// if some status are selected for a field it should be disabled for other status fields except for the current field
// e.g. if open is selected for status field then it should be disabled for confirmed, mitigated, etc.
export const getStatusFieldOptions = (
  formData: JiraSecurityToolRecordProps,
  currentFieldKey: string,
  options: SelectFieldOptionProps[],
) => {
  if (isEmpty(formData) && isEmpty(options)) {
    return [];
  }
  const selectedInOtherFields: string[] = [];

  for (const fieldKey in formData) {
    const formDataIndexKey = fieldKey as keyof JiraSecurityToolRecordProps;
    if (STATUS_FIELD_KEYS.includes(fieldKey)) {
      if (formData[formDataIndexKey] && fieldKey !== currentFieldKey) {
        const values = _.isArray(formData[formDataIndexKey])
          ? (formData[formDataIndexKey] as string[])
          : [formData[formDataIndexKey] as string];
        selectedInOtherFields.push(...values);
      }
    }
  }

  return options.map((option) => ({
    ...option,
    disabled: selectedInOtherFields.includes(option.value),
  }));
};
export const getCustomSeverityFields = ({
  onSeverityFieldChange,
  severityFieldOptions,
  severities,
}: {
  onSeverityFieldChange: (value: string) => void;
  severityFieldOptions: SelectFieldOptionProps[];
  severities: SelectFieldOptionProps[];
}) => {
  return [
    {
      type: FieldTypes.GROUP,
      fields: [
        {
          label: (
            <>
              <Vocabulary name="severity" /> Mapping Fields
            </>
          ),
          name: "customSeverity",
          options: severityFieldOptions || [],
          type: FieldTypes.SELECT,
          colSpan: 18,
          labelCol: { span: 8, className: "break-content" },
          wrapperCol: { span: 17 },
          props: {
            onChange: onSeverityFieldChange,
            allowClear: true,
          },
        },
        {
          render: (props: any) => <div className="p-y-sm" {...props} />,
          colSpan: 6,
        },
        {
          name: "critical",
          label: <Vocabulary name="criticalName" />,
          options: severities || [],
          type: FieldTypes.SELECT,
          ...commonPriorityFieldProps,
        },
        {
          name: "high",
          label: <Vocabulary name="highName" />,
          options: severities || [],
          type: FieldTypes.SELECT,
          ...commonPriorityFieldProps,
        },
        {
          name: "medium",
          label: <Vocabulary name="mediumName" />,
          options: severities || [],
          type: FieldTypes.SELECT,
          ...commonPriorityFieldProps,
        },
        {
          name: "low",
          label: <Vocabulary name="lowName" />,
          options: severities || [],
          type: FieldTypes.SELECT,
          ...commonPriorityFieldProps,
        },
        {
          name: "info",
          label: <Vocabulary name="infoName" />,
          options: severities || [],
          type: FieldTypes.SELECT,
          ...commonPriorityFieldProps,
        },
      ],
    },
  ];
};

export const getStatusMappingFields = ({
  getOptions,
  statusFieldOptions,
}: {
  getOptions: (
    options: SelectFieldOptionProps[],
    fieldKey: string,
  ) => SelectFieldOptionProps[];
  statusFieldOptions: SelectFieldOptionProps[];
}) => {
  return [
    {
      label: "Status Mapping Fields",
      type: FieldTypes.GROUP,
      style: { marginTop: 15 },
      fields: [
        {
          name: "open",
          label: <Vocabulary name="openName" />,
          props: {
            optionsMutator: (options: SelectFieldOptionProps[]) =>
              getOptions(options, "open"),
            allowClear: true,
          },
          options: statusFieldOptions || [],
          type: FieldTypes.MULTI_SELECT,
          ...statusSeverityProps,
        },
        {
          name: "confirmed",
          label: <Vocabulary name="confirmedName" />,
          props: {
            allowClear: true,
            optionsMutator: (options: SelectFieldOptionProps[]) =>
              getOptions(options, "confirmed"),
          },
          options: statusFieldOptions || [],
          type: FieldTypes.MULTI_SELECT,
          ...statusSeverityProps,
        },
        {
          name: "acceptRisk",
          label: <Vocabulary name="acceptRiskName" />,
          props: {
            allowClear: true,
            optionsMutator: (options: SelectFieldOptionProps[]) =>
              getOptions(options, "acceptRisk"),
          },
          options: statusFieldOptions || [],
          type: FieldTypes.MULTI_SELECT,
          ...statusSeverityProps,
        },
        {
          name: "suppressed",
          label: <Vocabulary name="suppressedName" />,
          props: {
            allowClear: true,
            optionsMutator: (options: SelectFieldOptionProps[]) =>
              getOptions(options, "suppressed"),
          },
          options: statusFieldOptions || [],
          type: FieldTypes.MULTI_SELECT,
          ...statusSeverityProps,
        },
        {
          name: "mitigated",
          label: <Vocabulary name="mitigatedName" />,
          props: {
            allowClear: true,
            optionsMutator: (options: SelectFieldOptionProps[]) =>
              getOptions(options, "mitigated"),
          },
          options: statusFieldOptions || [],
          type: FieldTypes.MULTI_SELECT,
          ...statusSeverityProps,
        },
        {
          name: "falsePositive",
          label: <Vocabulary name="falsePositiveName" />,
          props: {
            allowClear: true,
            optionsMutator: (options: SelectFieldOptionProps[]) =>
              getOptions(options, "falsePositive"),
          },
          options: statusFieldOptions || [],
          type: FieldTypes.MULTI_SELECT,
          ...statusSeverityProps,
        },
      ],
    },
  ];
};
